import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Usefetchdata from "hooks/usefetchdata";
import useModal from "components/modal/useModal";
import { Logout, SearchLoop, GroupPeople, ProfilIcon } from "assets/icons";
import DropdownMenu from "components/DropdownMenu/DropdownMenu";
import CommandPalette from "components/CommandPalette/CommandPalette";
import useCommandPalette from "hooks/useCommandPalette";
import { ADMIN_ROLE_LABELS } from "helper/roles";

const NavbarDesktop = () => {
    const {userData} = Usefetchdata();
    const {toggle} = useModal();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const { isOpen: isCommandPaletteOpen, open: openCommandPalette, close: closeCommandPalette } = useCommandPalette();
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    
    // Calculer la position du dropdown
    useEffect(() => {
        if (isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 4,
                right: window.innerWidth - rect.right
            });
        }
    }, [isOpen]);
    
    // Fermer le dropdown quand on clique ailleurs
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                triggerRef.current && 
                !triggerRef.current.contains(event.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);
    
    // Fonction pour obtenir les initiales
    const getInitials = () => {
        const firstname = userData?.firstname || '';
        const lastname = userData?.lastname || '';
        const firstInitial = firstname.charAt(0).toUpperCase();
        const lastInitial = lastname.charAt(0).toUpperCase();
        return firstInitial + lastInitial;
    };
    
    const getRoleName = () => ADMIN_ROLE_LABELS[userData?.role] || 'Utilisateur';
    
    const handleLogout = (e) => {
        e.preventDefault();
        toggle('log_d_logout');
    };
    
    const isAdmin = userData?.role === 500;

    const dropdownItems = [
        {
            icon: ProfilIcon,
            label: "Profil",
            onClick: () => { setIsOpen(false); navigate('/admin-profil'); }
        },
        { separator: true },
        ...(isAdmin ? [{
            icon: GroupPeople,
            label: "Console d'administration",
            onClick: () => { setIsOpen(false); navigate('/admin-console'); }
        }, { separator: true }] : []),
        {
            icon: Logout,
            label: 'Se déconnecter',
            onClick: handleLogout
        }
    ];
    
    return (
        <>
            {/* Search Button */}
            <button
                onClick={openCommandPalette}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors mr-3"
            >
                <SearchLoop wh={16} color="#6B7280" />
                <span className="hidden lg:inline">Rechercher</span>
                <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs bg-white rounded border border-gray-200 font-mono text-gray-400">
                    <span className="text-[10px]">⌘</span>K
                </kbd>
            </button>

            <button
                type="button"
                ref={triggerRef}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                className="relative flex items-center gap-3 cursor-pointer hover:bg-gray-100 rounded-lg px-2 py-1 text-left transition-colors select-none"
                onClick={() => setIsOpen(!isOpen)}
            >
                {/* Initiales */}
                <span className="min-w-[40px] w-[40px] h-[40px] rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                    <span className="text-sm font-medium text-gray-700">
                        {getInitials()}
                    </span>
                </span>
                {/* Informations utilisateur */}
                <span className="flex flex-col min-w-[120px]">
                    <span className="text-sm font-medium text-gray-700">
                        {userData?.firstname} {userData?.lastname}
                    </span>
                    <span className="text-xs text-gray-500">
                        {getRoleName()}
                    </span>
                </span>
            </button>
            
            {/* Dropdown menu - sous l'overlay du modal (z-40) quand "Vous nous quittez déjà" est ouvert */}
            {isOpen && ReactDOM.createPortal(
                <div 
                    ref={dropdownRef}
                    className="fixed"
                    style={{ 
                        top: dropdownPosition.top, 
                        right: dropdownPosition.right, 
                        zIndex: 30,
                        pointerEvents: 'auto'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <DropdownMenu items={dropdownItems} />
                </div>,
                document.getElementById('navbar-dropdown-portal') || document.body
            )}

            {/* Command Palette */}
            <CommandPalette isOpen={isCommandPaletteOpen} onClose={closeCommandPalette} />
        </>
    );
};

export default NavbarDesktop;